import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { retirarInteresseBolsa, avaliarParceiroBolsa, livresDoParceiro, pedirAula, minhasAulas, meusPedidosCapacitacao } from "../services/api";
import { EscolherHorario, quando, STATUS_AULA } from "./AulasComum";

// 🤝 CONFIANÇA NOS PARCEIROS DE CAPACITAÇÃO (22/10/2026) — pedaços da página /capacitacao/:slug:
//  • números calculados pelo eaiRH (o parceiro não edita), só a partir de 5 pedidos;
//  • avaliações dos alunos (nota 1–5 + comentário moderado) e a resposta pública do parceiro;
//  • avaliar ("A escola entrou em contato? Como foi?") e RETIRAR A AUTORIZAÇÃO (LGPD);
//  • professor com "sala do eaiRH": horários livres e pedido de aula grátis (fluxo da agenda).
// ⚠️ Nada aqui associa curso a vaga/emprego.

const tempo = (h) => {
  if (h == null) return null;
  if (h < 1) return "menos de 1 hora";
  if (h < 48) return `${Math.round(h)} hora${Math.round(h) === 1 ? "" : "s"}`;
  const d = Math.round(h / 24);
  return `${d} dias`;
};
const estrelas = (n) => "★★★★★".slice(0, Math.round(n)) + "☆☆☆☆☆".slice(0, 5 - Math.round(n));
const dataBr = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

export function NumerosParceiro({ numeros, prof }) {
  if (!numeros) return null;
  if (numeros.novo) {
    return (
      <section className="mt-4 bg-white rounded-2xl shadow-sm p-5 text-sm">
        <h2 className="font-bold text-text text-lg">Números no eaiRH</h2>
        <p className="mt-1 text-gray-600"><b>{prof ? "Professor(a) novo(a)" : "Parceiro novo"} no eaiRH.</b> Os números de atendimento aparecem a partir de {numeros.minimo} pedidos.</p>
      </section>
    );
  }
  const itens = [
    [String(numeros.pedidos), "pedidos pelo eaiRH"],
    [`${numeros.pctAtendidos}%`, "dos interessados atendidos"],
    [tempo(numeros.horasMediaAtendimento) || "—", "tempo médio até o atendimento"],
  ];
  return (
    <section className="mt-4 bg-white rounded-2xl shadow-sm p-5">
      <h2 className="font-bold text-text text-lg">Números no eaiRH</h2>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {itens.map(([v, r]) => (
          <div key={r} className="rounded-xl bg-slate-50 border border-gray-100 p-3">
            <div className="text-2xl font-extrabold text-text">{v}</div>
            <div className="text-xs text-gray-600">{r}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Calculados pelo eaiRH a partir dos pedidos feitos aqui. {prof ? "O professor" : "O parceiro"} não edita estes números.</p>
    </section>
  );
}

export function AvaliacoesParceiro({ avaliacoes, nome }) {
  if (!avaliacoes) return null;
  const { media, total, lista } = avaliacoes;
  return (
    <section id="avaliacoes" className="mt-4 bg-white rounded-2xl shadow-sm p-5 text-sm">
      <h2 className="font-bold text-text text-lg">Avaliações de alunos</h2>
      {total === 0 ? (
        <p className="mt-1 text-gray-500">Ainda sem avaliações. Quem pede contato pelo eaiRH recebe, uns dias depois, um convite para contar como foi.</p>
      ) : (
        <p className="mt-1"><span className="text-amber-500 text-lg">{estrelas(media)}</span> <b>{String(media).replace(".", ",")}</b> de 5 · {total} avaliação{total > 1 ? "ões" : ""}</p>
      )}
      {lista?.length > 0 && (
        <ul className="mt-3 divide-y">
          {lista.map((a) => (
            <li key={a.id} className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-amber-500">{estrelas(a.nota)}</span>
                <b>{a.nome}</b>
                <span className="text-xs text-gray-500">· {dataBr(a.criadoEm)}</span>
                {a.contatou === true && <span className="rounded-full bg-green-100 text-green-800 text-[11px] font-semibold px-2 py-0.5">recebeu contato</span>}
                {a.contatou === false && <span className="rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5">não recebeu contato</span>}
              </div>
              {a.comentario && <p className="mt-1 text-gray-700 whitespace-pre-line">{a.comentario}</p>}
              {a.resposta && (
                <div className="mt-2 ml-3 border-l-4 border-primary/40 pl-3 text-gray-700">
                  <div className="text-xs font-semibold text-gray-500">Resposta de {nome}{a.respostaEm ? ` · ${dataBr(a.respostaEm)}` : ""}</div>
                  <p className="whitespace-pre-line">{a.resposta}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-gray-400 mt-2">Só avalia quem pediu contato pelo eaiRH. Comentários passam pela moderação antes de aparecer; o parceiro pode responder.</p>
    </section>
  );
}

// "A escola entrou em contato? Como foi?" — uma avaliação por pedido.
export function AvaliarParceiro({ slug, prof, nome, abrirJa, onFeito }) {
  const [aberto, setAberto] = useState(!!abrirJa);
  const [nota, setNota] = useState(0);
  const [contatou, setContatou] = useState(null);
  const [comentario, setComentario] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setErro(""); setEnviando(true);
    try { const r = await avaliarParceiroBolsa(slug, { nota, contatou, comentario }); onFeito({ nota, contatou, status: r.status, comentario }); }
    catch (e) { setErro(e.message); }
    setEnviando(false);
  };
  if (!aberto) {
    return <button onClick={() => setAberto(true)} className="mt-3 bg-white border border-green-300 text-green-900 font-bold rounded-lg px-3 py-1.5 text-sm">⭐ Avaliar {prof ? nome : "a escola"}</button>;
  }
  return (
    <div className="mt-3 rounded-xl bg-white border border-gray-200 p-3 text-gray-800">
      <p className="font-bold">{prof ? `${nome} entrou em contato?` : "A escola entrou em contato?"}</p>
      <div className="flex gap-2 mt-1">
        {[[true, "Sim"], [false, "Ainda não"]].map(([v, r]) => (
          <button key={r} type="button" onClick={() => setContatou(v)}
            className={`rounded-lg border px-3 py-1 text-sm font-semibold ${contatou === v ? "bg-brand text-white border-brand" : "border-gray-300"}`}>{r}</button>
        ))}
      </div>
      <p className="font-bold mt-3">Como foi?</p>
      <div className="flex gap-1 mt-1" role="radiogroup" aria-label="Nota de 1 a 5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={nota === n} aria-label={`${n} de 5`} onClick={() => setNota(n)}
            className={`text-2xl leading-none ${n <= nota ? "text-amber-500" : "text-gray-300"}`}>★</button>
        ))}
      </div>
      <label className="block text-xs text-gray-600 mt-3">Comentário curto (opcional)
        <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} maxLength={500} rows={3}
          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 mt-1" placeholder="Como foi o atendimento? A aula ou a conversa ajudou?" />
      </label>
      <p className="text-[11px] text-gray-400">Seu primeiro nome aparece com a avaliação. O comentário passa pela moderação do eaiRH antes de ir para a página (e-mail, telefone e ofensas são escondidos).</p>
      {erro && <p className="text-red-600 mt-2">{erro}</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={() => setAberto(false)} className="bg-slate-100 font-bold rounded-lg px-3 py-1.5 text-sm">Depois</button>
        <button disabled={!nota || enviando} onClick={enviar} className="bg-accent text-white font-bold rounded-lg px-3 py-1.5 text-sm disabled:opacity-40">{enviando ? "Enviando…" : "Enviar avaliação"}</button>
      </div>
    </div>
  );
}

// LGPD: retirar a autorização e excluir o pedido. Apaga nome/e-mail/telefone e avisa o parceiro para apagar também.
export function RetirarAutorizacao({ slug, quem, compartilhado, onRetirado, compacto }) {
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const retirar = async () => {
    setErro(""); setEnviando(true);
    try { const r = await retirarInteresseBolsa(slug); onRetirado(r); }
    catch (e) { setErro(e.message); }
    setEnviando(false);
  };
  if (!confirmando) {
    return <button onClick={() => setConfirmando(true)} className={`${compacto ? "" : "mt-3 "}text-xs font-semibold text-red-700 underline`}>Retirar autorização e excluir meu pedido</button>;
  }
  return (
    <div className="mt-3 rounded-xl bg-red-50 border border-red-200 text-red-900 p-3 text-sm">
      <p className="font-bold">Retirar a autorização?</p>
      <p className="mt-1">O eaiRH apaga o seu pedido{compartilhado ? <> (nome, e-mail <b className="break-all">{compartilhado.email}</b> e telefone <b>{compartilhado.telefone}</b>)</> : ""} e avisa {quem} para apagar os seus dados e não entrar mais em contato. Sua avaliação, se houver, também sai.</p>
      <p className="mt-1 text-xs">Se mudar de ideia depois, é só pedir o contato de novo.</p>
      {erro && <p className="text-red-700 mt-2">{erro}</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={() => setConfirmando(false)} className="bg-white border border-gray-300 font-bold rounded-lg px-3 py-1.5">Voltar</button>
        <button disabled={enviando} onClick={retirar} className="bg-red-600 text-white font-bold rounded-lg px-3 py-1.5 disabled:opacity-50">{enviando ? "Excluindo…" : "Retirar e excluir"}</button>
      </div>
    </div>
  );
}

// 🎥 Professor com "sala do eaiRH": escolher um horário livre e pedir a aula grátis (o professor aprova ou sugere outro;
// confirmada, os dois recebem o link pessoal da sala e o convite na agenda). Mesmo fluxo de /capacitacao.
export function AgendaAulaGratis({ agendaId, nome, curso, user, ehCandidato, onEntrar, onPreferirContato }) {
  const [livres, setLivres] = useState(null);
  const [escolha, setEscolha] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [minha, setMinha] = useState(null);
  const [pronto, setPronto] = useState(false);
  const skills = (curso || "").split(/,\s*| e /).map((s) => s.trim()).filter(Boolean);
  const [skill, setSkill] = useState(skills[0] || "");

  useEffect(() => { livresDoParceiro(agendaId).then(setLivres).catch(() => setLivres([])); }, [agendaId, pronto]);
  useEffect(() => {
    if (!ehCandidato) { setMinha(null); return; }
    minhasAulas().then((l) => setMinha((l || []).find((a) => a.parceiroId === agendaId && !["recusada", "cancelada", "expirada"].includes(a.status)) || null)).catch(() => {});
  }, [agendaId, ehCandidato, pronto]);

  const enviar = async () => {
    setErro("");
    if (!user) { onEntrar(); return; }
    if (!ehCandidato) { setErro("Pedir aula é para contas de candidato."); return; }
    setEnviando(true);
    try { await pedirAula({ parceiroId: agendaId, inicio: escolha, skill, mensagem }); setPronto(true); setEscolha(null); }
    catch (e) { setErro(e.message); }
    setEnviando(false);
  };

  if (minha) {
    return (
      <div className="rounded-xl bg-green-50 border border-green-200 text-green-900 text-sm p-4">
        <p className="font-bold">{pronto ? "Pedido enviado! " : ""}Sua aula grátis com {nome}: {quando(minha.inicio)} · {STATUS_AULA[minha.status]?.txt || minha.status}</p>
        <p className="mt-1">{minha.status === "pedida" ? "O professor tem até 24 horas para aprovar ou sugerir outro horário; você recebe o aviso por e-mail." : "O link da sala e o convite de agenda chegam por e-mail."}</p>
        <Link to="/capacitacao" className="inline-block mt-2 font-semibold text-primary underline">Ver em Minhas aulas (remarcar, cancelar, entrar na sala)</Link>
      </div>
    );
  }
  return (
    <div className="text-sm">
      <h2 className="font-bold text-text text-lg">Escolha o horário da aula grátis</h2>
      <p className="text-gray-600 mt-1">A aula é na sala de vídeo do eaiRH, direto no navegador. {nome} aprova ou sugere outro horário em até 24 horas; confirmada, você recebe o link pessoal da sala e o convite na agenda.</p>
      {skills.length > 1 && (
        <label className="block text-xs text-gray-600 mt-3">Curso
          <select value={skill} onChange={(e) => setSkill(e.target.value)} className="w-full sm:w-64 border rounded-lg px-2 py-1.5 mt-1">
            {skills.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
      )}
      {livres === null ? <p className="text-gray-400 mt-2">Carregando horários…</p>
        : <EscolherHorario livres={livres} valor={escolha} onChange={setEscolha} vazio={`${nome} não tem horário livre nos próximos 14 dias.`} />}
      {escolha && (
        <label className="block text-xs text-gray-600 mt-3">Recado para o professor (opcional)
          <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} maxLength={500} rows={2}
            className="w-full border rounded-lg px-2 py-1.5 mt-1" placeholder="Ex.: sou iniciante e quero treinar conversação" />
        </label>
      )}
      {erro && <p className="text-red-600 mt-2">{erro}</p>}
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button disabled={!escolha || enviando} onClick={enviar} className="bg-accent text-white font-bold rounded-lg px-5 py-2.5 disabled:opacity-40">{enviando ? "Enviando…" : "Pedir aula grátis"}</button>
        <button type="button" onClick={onPreferirContato} className="text-xs font-semibold text-primary underline">Prefiro que {nome} entre em contato para combinar</button>
      </div>
    </div>
  );
}

// Minha conta › pedidos a parceiros de capacitação: o que foi compartilhado, com quem, e o botão de retirar (LGPD).
// Só aparece se houver pedido.
export function MeusPedidosCapacitacao() {
  const [lista, setLista] = useState(null);
  const carregar = () => meusPedidosCapacitacao().then(setLista).catch(() => setLista([]));
  useEffect(() => { carregar(); }, []);
  if (!lista || lista.length === 0) return null;
  return (
    <section className="mt-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-bold text-gray-900 mb-1">Pedidos de contato a escolas e professores</h2>
      <p className="text-sm text-gray-600 mb-3">Com a sua autorização, o eaiRH passou só o seu nome, e-mail e telefone a estes parceiros de capacitação. Você pode retirar a autorização quando quiser: o pedido é excluído e o parceiro é avisado para apagar os seus dados.</p>
      <ul className="divide-y">
        {lista.map((x) => (
          <li key={x.slug} className="py-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/capacitacao/${x.slug}`} className="font-semibold text-primary">{x.nome}</Link>
              <span className="text-gray-500">· {x.curso} · pedido em {dataBr(x.criadoEm)}</span>
            </div>
            <div className="text-xs text-gray-600 break-all">Compartilhado: {x.email} · {x.telefone}</div>
            <RetirarAutorizacao slug={x.slug} quem={x.tipo === "prof" ? x.nome : `a escola ${x.nome}`} compartilhado={{ email: x.email, telefone: x.telefone }}
              onRetirado={carregar} />
          </li>
        ))}
      </ul>
    </section>
  );
}
